import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { api, apiErr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowRight, RefreshCw, FlaskConical } from "lucide-react";

const BG =
  "https://images.unsplash.com/photo-1549791084-5f78368b208b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { user, login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [localLoginDisabled, setLocalLoginDisabled] = useState(false);
  const [fpOpen, setFpOpen] = useState(false);
  const [fpEmail, setFpEmail] = useState("");
  const [captcha, setCaptcha] = useState(null); // {captchaId, question}
  const [fpAnswer, setFpAnswer] = useState("");
  const [fpErr, setFpErr] = useState("");
  const [fpLoading, setFpLoading] = useState(false);
  const [fpSent, setFpSent] = useState(false);
  const [demo, setDemo] = useState({ mode: false, accounts: [] });

  const fillDemo = (acc) => { setEmail(acc.email); setPassword(acc.password); };

  const loadCaptcha = () => {
    setFpAnswer("");
    api.get("/auth/captcha").then((r) => setCaptcha(r.data)).catch(() => setCaptcha(null));
  };

  const openForgot = () => {
    setFpErr(""); setFpSent(false); setFpEmail(email);
    loadCaptcha();
    setFpOpen(true);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setFpErr("");
    setFpLoading(true);
    try {
      await api.post("/auth/forgot-password", {
        email: fpEmail, captchaId: captcha?.captchaId, captchaAnswer: fpAnswer,
      });
      setFpSent(true);
    } catch (e2) {
      setFpErr(apiErr(e2));
      loadCaptcha();
    } finally {
      setFpLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const e = params.get("error");
    if (e) setErr(decodeURIComponent(e));
    api.get("/auth/setup-status").then((r) => {
      if (r.data.needsSetup && !r.data.localLoginDisabled) nav("/setup");
      if (r.data.localLoginDisabled) setLocalLoginDisabled(true);
      if (r.data.demoMode) setDemo({ mode: true, accounts: r.data.demoAccounts || [] });
    }).catch(() => {});
    api.get("/auth/sso/status").then((r) => {
      setSsoEnabled(!!r.data.enabled);
      if (r.data.localLoginDisabled) setLocalLoginDisabled(true);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (e2) {
      setErr(apiErr(e2));
    } finally {
      setLoading(false);
    }
  };

  const ssoLogin = () => {
    window.location.href = `${API}/auth/sso/login`;
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="flex items-center justify-center bg-white px-5 py-10 sm:px-6 sm:py-12">
        <div className="w-full max-w-sm">
          <div className="mb-10 flex items-center gap-2.5">
            <img src="/logo.svg" alt="Scalio Nexus" className="h-9 w-9 shrink-0" />
            <div className="leading-none">
              <div className="font-display text-base font-black uppercase tracking-tight text-zinc-950">
                Scalio Nexus
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                {t("brand.subtitle")}
              </div>
            </div>
          </div>

          <h1 className="font-display text-3xl font-black uppercase tracking-tighter text-zinc-950">
            {t("login.signIn")}
          </h1>
          <p className="mt-2 text-sm text-zinc-500">{t("login.subtitle")}</p>

          <form onSubmit={submit} className={`mt-8 space-y-5${localLoginDisabled ? " hidden" : ""}`}>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                {t("login.email")}
              </Label>
              <Input
                id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="rounded-sm border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="login-email-input" required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                {t("login.password")}
              </Label>
              <Input
                id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="rounded-sm border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="login-password-input" required
              />
            </div>

            <div className="-mt-1 text-right">
              {!demo.mode && (
                <button
                  type="button" onClick={openForgot} data-testid="forgot-password-link"
                  className="text-xs font-semibold text-primary hover:underline"
                >
                  Forgot password?
                </button>
              )}
            </div>

            {err && (
              <div className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
                {err}
              </div>
            )}

            <Button
              type="submit" disabled={loading} data-testid="login-submit-button"
              className="group h-11 w-full rounded-sm bg-primary text-sm font-semibold uppercase tracking-wide hover:bg-[#002480]"
            >
              {loading ? t("login.signingIn") : t("login.signIn")}
              <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </form>

          {ssoEnabled && (
            <>
              {!localLoginDisabled && (
                <div className="my-6 flex items-center gap-3">
                  <div className="h-px flex-1 bg-zinc-200" />
                  <span className="text-xs uppercase tracking-wider text-zinc-400">{t("login.or")}</span>
                  <div className="h-px flex-1 bg-zinc-200" />
                </div>
              )}
              {localLoginDisabled && (
                <p className="mb-6 mt-8 text-sm text-zinc-500" data-testid="sso-only-notice">{t("login.ssoOnly")}</p>
              )}
              <Button
                type="button" variant="outline" onClick={ssoLogin} data-testid="sso-login-button"
                className="h-11 w-full rounded-sm border-zinc-300 text-sm font-semibold"
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 23 23" aria-hidden="true">
                  <rect width="10" height="10" x="1" y="1" fill="#F25022" />
                  <rect width="10" height="10" x="12" y="1" fill="#7FBA00" />
                  <rect width="10" height="10" x="1" y="12" fill="#00A4EF" />
                  <rect width="10" height="10" x="12" y="12" fill="#FFB900" />
                </svg>
                {t("login.microsoft")}
              </Button>
            </>
          )}

          {localLoginDisabled && !ssoEnabled && (
            <div className="mt-8 rounded-sm border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800" data-testid="sso-unavailable-notice">
              {t("login.ssoUnavailable")}
            </div>
          )}

          {demo.mode && (
            <div className="mt-8 rounded-sm border border-sky-200 bg-sky-50/70 p-4" data-testid="demo-credentials">
              <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-sky-700">
                <FlaskConical size={14} /> {t("demo.bannerTitle")}
              </div>
              <p className="mb-3 text-xs text-sky-700/80">{t("demo.loginHint")}</p>
              <div className="space-y-1.5">
                {demo.accounts.map((a) => (
                  <button
                    key={a.email} type="button" onClick={() => fillDemo(a)}
                    data-testid={`demo-account-${a.role}`}
                    className="flex w-full items-center justify-between gap-3 rounded-sm border border-sky-200 bg-white px-3 py-2 text-left transition-colors hover:border-sky-400 hover:bg-sky-50"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-xs font-semibold text-zinc-800">{a.label}</div>
                      <div className="truncate font-mono text-[11px] text-zinc-500">{a.email}</div>
                    </div>
                    <div className="shrink-0 font-mono text-[11px] text-zinc-400">{a.password}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="relative hidden lg:block">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/85 mix-blend-multiply" />
        <div className="relative flex h-full flex-col justify-end p-12 text-white">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/70">{t("login.tagline")}</div>
          <h2 className="mt-4 max-w-md font-display text-4xl font-black uppercase leading-[1.05] tracking-tighter">
            {t("login.govern")}
          </h2>
          <p className="mt-4 max-w-md text-sm text-white/80">{t("login.description")}</p>
        </div>
      </div>

      <Dialog open={fpOpen} onOpenChange={setFpOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Reset your password</DialogTitle>
            <DialogDescription>
              Enter your account email and solve the check. We'll send a reset link if the account exists.
            </DialogDescription>
          </DialogHeader>
          {fpSent ? (
            <div className="space-y-3 py-2" data-testid="forgot-sent">
              <div className="rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-800">
                If an account exists for <strong>{fpEmail}</strong>, a password reset link is on its way. The link expires in 30 minutes.
              </div>
              <DialogFooter>
                <Button onClick={() => setFpOpen(false)} className="rounded-sm bg-primary hover:bg-[#002480]" data-testid="forgot-close">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Email</Label>
                <Input
                  type="email" value={fpEmail} onChange={(e) => setFpEmail(e.target.value)}
                  className="rounded-sm border-zinc-300" data-testid="forgot-email-input" required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                  Security check: what is {captcha?.question || "…"}?
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={fpAnswer} onChange={(e) => setFpAnswer(e.target.value)}
                    className="rounded-sm border-zinc-300" data-testid="forgot-captcha-input"
                    placeholder="Answer" required
                  />
                  <button
                    type="button" onClick={loadCaptcha} data-testid="forgot-captcha-refresh"
                    className="flex h-9 w-9 shrink-0 items-center justify-center border border-zinc-300 text-zinc-500 hover:bg-zinc-50"
                    title="New question"
                  >
                    <RefreshCw size={15} />
                  </button>
                </div>
              </div>
              {fpErr && (
                <div className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="forgot-error">
                  {fpErr}
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setFpOpen(false)} className="rounded-sm">Cancel</Button>
                <Button type="submit" disabled={fpLoading} data-testid="forgot-submit-button" className="rounded-sm bg-primary hover:bg-[#002480]">
                  {fpLoading ? "Sending…" : "Send reset link"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
